import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Button, 
  List, 
  Tag, 
  Spin, 
  message, 
  Modal, 
  Form, 
  Input, 
  Select,
  Space,
  Divider,
  Popconfirm
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  UserOutlined,
  MailOutlined,
  ArrowLeftOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { CitadeloLayout } from '../../components/CitadeloLayout';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ProjectType {
  id: number;
  name: string;
}

interface Project {
  id: number;
  pid: string;
  client?: {
    id: number;
    name: string;
    contactName?: string;
    contactFirstName?: string;
    contactLastName?: string;
  };
  projectTypes?: ProjectType[];
  customProjectType?: string;
}

interface Contact {
  id: number;
  name: string;
  email: string;
  role?: string;
}

interface DistributionGroup {
  id: number;
  groupName: string;
  description: string;
  contacts: Contact[];
}

export const DistributionManagement: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { logout } = useAuth();
  
  const [project, setProject] = useState<Project | null>(null);
  const [distributionGroups, setDistributionGroups] = useState<DistributionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingGroup, setEditingGroup] = useState<DistributionGroup | null>(null);
  
  const [allGroups, setAllGroups] = useState<DistributionGroup[]>([]);
  const [reuseModalVisible, setReuseModalVisible] = useState(false);
  const [reuseForm] = Form.useForm();

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching project with ID:', projectId);
      const response = await apiService.getProjectById(projectId!);
      console.log('Project fetched successfully:', response.data);
      setProject(response.data);
      
      // Use distribution groups from project response if available AND populated, otherwise fetch separately
      const hasPopulatedGroups = response.data.distributionGroups && 
        response.data.distributionGroups.every((g: any) => Array.isArray(g.contacts));

      if (hasPopulatedGroups && response.data.distributionGroups) {
        console.log('Using distribution groups from project response:', response.data.distributionGroups);
        setDistributionGroups(response.data.distributionGroups);
      } else {
        console.log('Distribution groups in project response are incomplete, fetching from API...');
        fetchDistributionGroups(response.data.pid);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      message.error('Failed to load project information');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    const projectFromState = location.state?.project;
    if (projectFromState) {
      console.log('Using project from state:', projectFromState);
      setProject(projectFromState);
      setLoading(false);
      
      // Use distribution groups from project state if available AND has contacts populated, otherwise fetch separately
      const hasPopulatedGroups = projectFromState.distributionGroups && 
        projectFromState.distributionGroups.every((g: any) => Array.isArray(g.contacts));

      if (hasPopulatedGroups && projectFromState.distributionGroups) {
        console.log('Using distribution groups from project state:', projectFromState.distributionGroups);
        setDistributionGroups(projectFromState.distributionGroups);
      } else {
        console.log('Distribution groups in state are incomplete, fetching from API...');
        fetchDistributionGroups(projectFromState.pid);
      }
    } else if (projectId) {
      fetchProject();
    }
  }, [projectId, location.state, fetchProject]);

  const fetchDistributionGroups = async (pid: string) => {
    try {
      console.log('Fetching distribution groups for PID:', pid);
      const response = await apiService.getProjectDistributionGroups(pid);
      console.log('Distribution groups fetched:', response);
      setDistributionGroups(response);
    } catch (error) {
      console.error('Error fetching distribution groups:', error);
      message.error('Failed to load distribution groups');
    }
  };

  const handleCreateOrUpdateGroup = async (values: any) => {
    if (!project?.pid) return;
    
    setModalLoading(true);
    try {
      if (editingGroup) {
        // Transform email strings to contact objects if needed (handled by API/backend usually, 
        // but let's match what Create does or what backend expects)
        // Backend update for component expects array of component data
        const contacts = values.contacts.map((contact: string | Contact) => {
            if (typeof contact === 'string') {
                return {
                    name: contact.split('@')[0],
                    email: contact,
                    role: 'other'
                };
            }
            return contact;
        });

        await apiService.updateDistributionGroup(editingGroup.id, {
            ...values,
            contacts
        });
        message.success('Distribution group updated successfully');
      } else {
        await apiService.createProjectDistributionGroup(project.pid, values);
        message.success('Distribution group created successfully');
      }
      
      setModalVisible(false);
      setEditingGroup(null);
      form.resetFields();
      fetchDistributionGroups(project.pid);
    } catch (error) {
      console.error(error);
      message.error(`Failed to ${editingGroup ? 'update' : 'create'} distribution group`);
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditGroup = (group: DistributionGroup) => {
    setEditingGroup(group);
    form.setFieldsValue({
        groupName: group.groupName,
        description: group.description,
        contacts: group.contacts?.map(c => c.email) || []
    });
    setModalVisible(true);
  };

  const openReuseModal = async () => {
    try {
        // Filter by client if available
        const clientId = project?.client?.id;
        let response;
        if (clientId) {
          response = await apiService.getDistributionGroupsByClient(clientId);
        } else {
          response = await apiService.getAllDistributionGroups();
        }
        // Filter out groups already in the project
        const currentIds = distributionGroups.map(g => g.id);
        const available = response.data.filter(g => !currentIds.includes(g.id));
        setAllGroups(available);
        setReuseModalVisible(true);
    } catch (error) {
        message.error('Failed to load available groups');
    }
  };

  const handleReuseSubmit = async (values: any) => {
    if (!project?.pid) return;
    try {
        const currentIds = distributionGroups.map(g => g.id);
        const newIds = [...currentIds, ...values.selectedGroups];
        await apiService.updateProjectDistributionGroups(project.pid, newIds);
        message.success('Groups linked successfully');
        setReuseModalVisible(false);
        reuseForm.resetFields();
        fetchDistributionGroups(project.pid);
    } catch (error) {
        message.error('Failed to link groups');
    }
  };

  const handleUnlinkGroup = async (groupId: number) => {
    if (!project?.pid) return;
    try {
        const currentIds = distributionGroups.map(g => g.id);
        const newIds = currentIds.filter(id => id !== groupId);
        await apiService.updateProjectDistributionGroups(project.pid, newIds);
        message.success('Group removed from project');
        fetchDistributionGroups(project.pid);
    } catch (error) {
        message.error('Failed to remove group');
    }
  };


  const handleRefresh = () => {
    if (project?.pid) {
      fetchDistributionGroups(project.pid);
    } else if (projectId) {
      fetchProject();
    }
  };

  if (loading) {
    return (
      <CitadeloLayout onLogout={logout}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>Loading project information...</Text>
          </div>
        </div>
      </CitadeloLayout>
    );
  }

  if (!project) {
    return (
      <CitadeloLayout onLogout={logout}>
        <Card>
          <Text type="danger">Project not found</Text>
        </Card>
      </CitadeloLayout>
    );
  }

  return (
    <CitadeloLayout onLogout={logout} onRefresh={handleRefresh}>
      <div>
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Distribution Groups
            </Title>
            <Text type="secondary">
              Manage email distribution lists for project: {project.client?.name} - {project.pid}
            </Text>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => {
                  setEditingGroup(null);
                  form.resetFields();
                  setModalVisible(true);
              }}
            >
              Create Group
            </Button>
            <Button 
              icon={<TeamOutlined />}
              onClick={openReuseModal}
            >
              Add Existing
            </Button>
            <Button 
              onClick={() => navigate('/projects')}
              icon={<ArrowLeftOutlined />}
            >
              Back to Projects
            </Button>
          </div>
        </div>
      </Card>

      {distributionGroups.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <MailOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
            <Title level={4}>No Distribution Groups</Title>
            <Text type="secondary">
              Create your first distribution group to manage email lists for this project.
            </Text>
          </div>
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2 }}
          dataSource={distributionGroups}
          renderItem={(group) => (
            <List.Item>
              <Card
                title={group.groupName}
                extra={
                  <Space>
                    <Button 
                        size="small" 
                        icon={<EditOutlined />} 
                        onClick={() => handleEditGroup(group)}
                    >
                      Edit
                    </Button>
                    <Popconfirm
                        title="Remove group"
                        description="Are you sure you want to remove this group from the project?"
                        onConfirm={() => handleUnlinkGroup(group.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />}>
                          Remove
                        </Button>
                    </Popconfirm>
                  </Space>
                }
              >
                {group.description && (
                  <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                    {group.description}
                  </Text>
                )}
                
                <Divider style={{ margin: '12px 0' }} />
                
                <div>
                  <Text strong>
                    <UserOutlined style={{ marginRight: '4px' }} />
                    Contacts ({group.contacts?.length || 0})
                  </Text>
                  {group.contacts?.length > 0 ? (
                    <div style={{ marginTop: '8px' }}>
                      {group.contacts.map((contact, index) => (
                        <div key={index} style={{ marginBottom: '4px' }}>
                          <Text>
                            <MailOutlined style={{ marginRight: '4px' }} />
                            {contact.name} ({contact.email})
                          </Text>
                          {contact.role && (
                            <Tag style={{ marginLeft: '8px', fontSize: '12px' }}>
                              {contact.role}
                            </Tag>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                      No contacts added yet
                    </Text>
                  )}
                </div>
              </Card>
            </List.Item>
          )}
        />
      )}

      <Modal
        title={editingGroup ? "Edit Distribution Group" : "Create Distribution Group"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingGroup(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={modalLoading}
        okText={editingGroup ? "Update Group" : "Create Group"}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateOrUpdateGroup}
        >
          <Form.Item
            name="groupName"
            label="Group Name"
            rules={[{ required: true, message: 'Please enter group name' }]}
          >
            <Input placeholder="e.g., Management Team, Technical Team" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Optional description of this group" />
          </Form.Item>

          <Form.Item
            name="contacts"
            label="Contacts"
          >
            <Select
              mode="tags"
              placeholder="Add email addresses"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add Existing Group"
        open={reuseModalVisible}
        onCancel={() => {
            setReuseModalVisible(false);
            reuseForm.resetFields();
        }}
        onOk={() => reuseForm.submit()}
        okText="Add Selected Groups"
      >
        <Form
            form={reuseForm}
            layout="vertical"
            onFinish={handleReuseSubmit}
        >
            <Form.Item
                name="selectedGroups"
                label="Select Groups"
                rules={[{ required: true, message: 'Please select at least one group' }]}
            >
                <Select
                    mode="multiple"
                    placeholder="Select groups to add"
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                >
                    {allGroups.map(group => (
                        <Select.Option key={group.id} value={group.id}>
                            {group.groupName} {group.description ? `- ${group.description}` : ''}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
        </Form>
      </Modal>
      </div>
    </CitadeloLayout>
  );
};
